import { useState } from "react";

import { Brain, Plus, Trash2, Edit, Sparkles, Loader2, ArrowUp } from "lucide-react";

import { ConventionPackLibrary } from "@/components/conventions/ConventionPackLibrary";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useMemories } from "@/hooks/useMemories";
import { MEMORY_CATEGORIES, MemoryCategory, getCategoryConfig } from "@/lib/memoryCategories";

import { MemoryHistory } from "./MemoryHistory";
import { MemoryInheritanceVisualization } from "./MemoryInheritanceVisualization";


interface Memory {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  version: number;
  category: MemoryCategory;
  enabled: boolean;
  workspace_id?: string | null;
  board_id?: string | null;
  folder_id?: string | null;
  tag_id?: string | null;
  project_id?: string | null;
}

interface MemoryManagerProps {
  workspaceId?: string;
  boardId?: string;
  folderId?: string;
  tagId?: string;
  projectId?: string;
  scopeLabel: string;
}

export const MemoryManager = ({
  workspaceId,
  boardId,
  folderId,
  tagId,
  projectId,
  scopeLabel,
}: MemoryManagerProps) => {
  const [open, setOpen] = useState(false);
  const [editingMemory, setEditingMemory] = useState<Memory | null>(null);
  const [content, setContent] = useState("");
  const [category, setCategory] = useState<MemoryCategory>("context");

  const scope = { workspaceId, boardId, folderId, tagId, projectId };
  const {
    memories,
    isLoading,
    saveMemory,
    isSaving,
    deleteMemory,
    toggleMemoryEnabled,
    promoteMemory,
    refineMemory,
    isRefining,
  } = useMemories(scope, open);
  
  const getScopeMessage = () => {
    if (workspaceId) return "in all sessions across this workspace";
    if (boardId) return "in all sessions for projects in this board";
    if (folderId) return "in all sessions for projects under this folder";
    if (tagId) return "in all sessions for projects with this tag";
    if (projectId) return "in all sessions for this project";
    return "in AI conversations";
  };

  const handleSave = async () => {
    await saveMemory({ memoryId: editingMemory?.id, content, category });
    if (!editingMemory) {
      setContent("");
      setCategory("context");
    }
    setEditingMemory(null);
  };

  const handleRefine = async () => {
    const refinedContent = await refineMemory(content);
    if (refinedContent) setContent(refinedContent);
  };

  const handleEdit = (memory: Memory) => {
    setEditingMemory(memory);
    setContent(memory.content);
    setCategory(memory.category);
  };

  const handleCancel = () => {
    setEditingMemory(null);
    setContent("");
    setCategory("context");
  };

  return (
    <div className="flex gap-2">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Brain className="h-4 w-4" />
            Memories
            {memories.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {memories.length}
              </Badge>
            )}
          </Button>
        </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Memories for {scopeLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {projectId && (
            <MemoryInheritanceVisualization
              workspaceId={workspaceId}
              boardId={boardId}
              folderId={folderId}
              projectId={projectId}
            />
          )}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : memories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No memories yet. Add context that will be included {getScopeMessage()}.
            </div>
          ) : (
            memories.map((memory) => {
              const categoryConfig = getCategoryConfig(memory.category);
              const CategoryIcon = categoryConfig.icon;
              
              const getScopeLabel = () => {
                if (memory.workspace_id) return "Workspace";
                if (memory.board_id) return "Board";
                if (memory.folder_id) return "Folder";
                if (memory.tag_id) return "Tag";
                if (memory.project_id) return "Project";
                return "";
              };

              const getScopeColor = () => {
                if (memory.workspace_id) return "oklch(0.75 0.15 270)"; // Purple
                if (memory.board_id) return "oklch(0.65 0.2 240)"; // Blue
                if (memory.folder_id) return "oklch(0.70 0.18 180)"; // Cyan
                if (memory.tag_id) return "oklch(0.72 0.20 150)"; // Green
                if (memory.project_id) return "oklch(0.65 0.20 30)"; // Orange
                return "oklch(0.65 0.1 0)"; // Gray
              };
              
              return (
                <Card key={memory.id} className={`p-4 ${!memory.enabled ? "opacity-60" : ""}`}>
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge 
                          variant="outline" 
                          className="gap-1"
                          style={{ 
                            borderColor: categoryConfig.color,
                            color: categoryConfig.color 
                          }}
                        >
                          <CategoryIcon className="h-3 w-3" />
                          {categoryConfig.label}
                        </Badge>
                        <Badge 
                          variant="secondary" 
                          className="text-xs"
                          style={{ 
                            backgroundColor: `${getScopeColor()}15`,
                            color: getScopeColor(),
                            borderColor: `${getScopeColor()}40`
                          }}
                        >
                          {getScopeLabel()}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={memory.enabled}
                          onCheckedChange={() => toggleMemoryEnabled({ memoryId: memory.id, currentEnabled: memory.enabled })}
                        />
                        <span className="text-xs text-muted-foreground">
                          {memory.enabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-start gap-4">
                      <p className="flex-1 whitespace-pre-wrap">{memory.content}</p>
                      <div className="flex gap-2">
                        <MemoryHistory
                          memoryId={memory.id}
                          currentVersion={memory.version}
                          onRestore={(content) => {
                            setEditingMemory(memory);
                            setContent(content);
                            setCategory(memory.category);
                          }}
                        />
                        {memory.project_id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Promote memory"
                              >
                                <ArrowUp className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {folderId && (
                                <DropdownMenuItem onClick={() => promoteMemory({ memory, targetScope: "folder" })}>
                                  Promote to Folder
                                </DropdownMenuItem>
                              )}
                              {boardId && (
                                <DropdownMenuItem onClick={() => promoteMemory({ memory, targetScope: "board" })}>
                                  Promote to Board
                                </DropdownMenuItem>
                              )}
                              {workspaceId && (
                                <DropdownMenuItem onClick={() => promoteMemory({ memory, targetScope: "workspace" })}>
                                  Promote to Workspace
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(memory)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteMemory(memory.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        <div className="space-y-4 border-t pt-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">
                {editingMemory ? "Edit Memory" : "Add New Memory"}
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefine}
                disabled={isRefining || !content.trim()}
                className="gap-2"
              >
                {isRefining ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Refine with AI
              </Button>
            </div>
            <Select value={category} onValueChange={(value) => setCategory(value as MemoryCategory)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(MEMORY_CATEGORIES).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" style={{ color: config.color }} />
                        <span>{config.label}</span>
                        <span className="text-xs text-muted-foreground">- {config.description}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Enter memory content... (e.g., key facts, preferences, context that should be remembered)"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
            />
          </div>
          <div className="flex justify-end gap-2">
            {editingMemory && (
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            )}
            <Button onClick={handleSave} disabled={isSaving || !content.trim()}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  {editingMemory ? (
                    <>
                      <Edit className="h-4 w-4 mr-2" />
                      Update
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Add
                    </>
                  )}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <ConventionPackLibrary
      workspaceId={workspaceId}
      boardId={boardId}
      folderId={folderId}
      tagId={tagId}
      projectId={projectId}
      scopeLabel={scopeLabel}
    />
  </div>
  );
};
